import React, { useLayoutEffect, useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../redux/store";
import { Loader2, ChevronDown, CheckCircle2, Clock, User } from "lucide-react";
import { setTopNavNameTitle } from "../../redux/features/mobileLayout";
import { NgwAvatar } from "../../components/common";
import { useNavigate } from "react-router";
import { useMediaQuery } from "@mui/material";
import { DateTimeUtil } from "../../utils";
import { getManagerEmployees } from "../../redux/features/timesheet/api";
import { updateTimecardApprovalStatus } from "../../redux/features/schedule/ScheduleApi";
import { UpdateApprovalStatusRequest } from "../../types";
import { toast } from "react-toastify";

interface TimesheetApprovalEmployee {
    employee_id?: string;
    emp_name?: string;
    profile_image_url?: string;
    total_hours?: string;
    approval_status?: string;
    approval_status_code?: string | number;
    approved_by_me?: boolean;
    start_date?: string;
    end_date?: string;
}

const TimesheetApprovalsNotificationDetail: React.FC = () => {
    const notificationDetail = useSelector((state: RootState) => state.notification.notificationDetail);
    const user = useSelector((state: RootState) => state.user.user);
    const isLoading = useSelector((state: RootState) => state.notification.isNotificationDetailLoading);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const isMobile = useMediaQuery("(max-width:768px)");

    const [statusFilter, setStatusFilter] = useState<string>("Pending");
    const [showFilterDropdown, setShowFilterDropdown] = useState(false);
    const [employees, setEmployees] = useState<TimesheetApprovalEmployee[]>([]);
    const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
    const [payPeriod, setPayPeriod] = useState<{ start: string; end: string } | null>(null);
    const [isApprovingAll, setIsApprovingAll] = useState(false);

    useLayoutEffect(() => {
        dispatch(setTopNavNameTitle("Timesheet Approvals"));
    }, [dispatch]);

    const fetchEmployeesForPayPeriod = useCallback(
        async (startDate: string, endDate: string) => {
            setIsLoadingEmployees(true);
            try {
                const payload = {
                    pay_period: `${startDate} - ${endDate}`,
                    employee_id: "",
                    fields: ["total_hours"],
                };
                const result = await dispatch(getManagerEmployees(payload));
                if (result.payload && Array.isArray(result.payload)) {
                    // Map the response to our TimesheetApprovalEmployee format
                    const mappedEmployees = result.payload.map((emp: any) => ({
                        employee_id: emp.emp_detail?.employee_id,
                        emp_name: emp.emp_detail?.emp_name,
                        profile_image_url: emp.emp_detail?.profile_image_url,
                        total_hours: emp.total_hours || "0",
                        approval_status: emp.approval_status || "Pending",
                        approved_by_me: emp.approved_by_me || false,
                        start_date: startDate,
                        end_date: endDate,
                    }));
                    setEmployees(mappedEmployees);
                }
            } catch (error) {
                console.error("Error fetching employees:", error);
            } finally {
                setIsLoadingEmployees(false);
            }
        },
        [dispatch]
    );

    useEffect(() => {
        // Extract timesheet approval data from notification detail
        const approvalDetails = (notificationDetail as any)?.approval_details;

        if (approvalDetails) {
            // Extract pay period from paycycles
            if (approvalDetails.paycycles) {
                const parts = approvalDetails.paycycles.split(" - ");
                if (parts.length === 2) {
                    setPayPeriod({ start: parts[0].trim(), end: parts[1].trim() });
                }
            }

            // Map employees from employees_list
            if (approvalDetails.employees_list && Array.isArray(approvalDetails.employees_list)) {
                const mappedEmployees = approvalDetails.employees_list.map((emp: any) => {
                    // Parse pay period dates for navigation
                    const payPeriodParts = approvalDetails.paycycles?.split(" - ") || [];
                    const startDate = payPeriodParts[0]?.trim() || "";
                    const endDate = payPeriodParts[1]?.trim() || "";

                    return {
                        employee_id: emp.os_emp_account_id,
                        emp_name: emp.emp_name,
                        profile_image_url: emp.profile_image_url || emp.image_url, // Handle both possible field names
                        total_hours: emp.total_worked?.total_hours || "0",
                        approval_status: emp.approval_text || emp.approval_status || "PENDING",
                        approval_status_code: emp.approval_status, // Keep numeric status for reference
                        approved_by_me: emp.approved_by_me || false, // May not be in response
                        start_date: startDate,
                        end_date: endDate,
                    };
                });
                setEmployees(mappedEmployees);
            }
        } else {
            // Fallback: Try to extract from alert_json or message_info
            const alertJson = notificationDetail?.alert_json;
            const messageInfo = (notificationDetail as any)?.message_info;

            // Try to get pay period from message_info.PAYCYCLES
            const payPeriodStr =
                messageInfo?.PAYCYCLES || (alertJson as any)?.PAYCYCLES || (alertJson as any)?.pay_period;
            if (payPeriodStr) {
                const parts = payPeriodStr.split(" - ");
                if (parts.length === 2) {
                    setPayPeriod({ start: parts[0].trim(), end: parts[1].trim() });
                    // Try to fetch employees if we have employee IDs
                    const empList = messageInfo?.EMP_LIST || (alertJson as any)?.EMP_LIST;
                    if (empList && Array.isArray(empList) && empList.length > 0) {
                        fetchEmployeesForPayPeriod(parts[0].trim(), parts[1].trim());
                    }
                }
            }
        }
    }, [notificationDetail, fetchEmployeesForPayPeriod]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (showFilterDropdown) {
                const target = event.target as HTMLElement;
                if (!target.closest(".filter-dropdown-container")) {
                    setShowFilterDropdown(false);
                }
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showFilterDropdown]);

    const formatHours = (hours?: string): string => {
        if (!hours) return "0,0 hrs";
        const numHours = parseFloat(hours);
        return `${numHours.toFixed(1).replace(".", ",")} hrs`;
    };

    const getStatusDisplay = (status?: string, statusCode?: string | number): { text: string; color: string } => {
        // Check numeric status code first (0 = pending, 1 = approved, etc.)
        if (statusCode !== undefined && statusCode !== null) {
            const code = typeof statusCode === "string" ? parseInt(statusCode) : statusCode;
            if (code === 1) {
                return { text: "Approved", color: "text-green-600" };
            } else if (code === 0) {
                return { text: "Pending", color: "text-orange-600" };
            }
        }

        // Fallback to text status
        const statusLower = (status || "").toLowerCase();
        if (statusLower === "approved" || statusLower === "approve") {
            return { text: "Approved", color: "text-green-600" };
        } else if (statusLower === "pending") {
            return { text: "Pending", color: "text-orange-600" };
        } else if (statusLower === "awaiting approval" || statusLower === "awaiting") {
            return { text: "Awaiting Approval", color: "text-yellow-600" };
        } else if (statusLower === "rejected" || statusLower === "reject") {
            return { text: "Rejected", color: "text-red-600" };
        }
        return { text: "Pending", color: "text-orange-600" };
    };

    const filteredEmployees = employees.filter((emp) => {
        if (statusFilter === "All") return true;
        const status = getStatusDisplay(emp.approval_status, emp.approval_status_code).text;
        return status.toLowerCase() === statusFilter.toLowerCase();
    });

    const handleApproveAll = async () => {
        if (!payPeriod || !payPeriod.start || !payPeriod.end) {
            toast.error("Pay period information is missing");
            return;
        }

        // Get all employee IDs that have valid employee_id
        const employeeIds = employees
            .map((emp) => emp.employee_id)
            .filter((id): id is string => !!id)
            .map((id) => Number(id))
            .filter((id) => !isNaN(id));

        if (employeeIds.length === 0) {
            toast.error("No employees found to approve");
            return;
        }

        setIsApprovingAll(true);
        try {
            // Extract date part only (YYYY-MM-DD) from datetime strings if present
            const startDate = payPeriod.start.split(" ")[0];
            const endDate = payPeriod.end.split(" ")[0];

            const payload: UpdateApprovalStatusRequest = {
                start_date: startDate,
                end_date: endDate,
                APPROVAL_STATUS: 1, // 1 = approved
                EMP_IDS: employeeIds,
                APPROVAL_NOTE: "",
            };

            const resultAction = await dispatch(updateTimecardApprovalStatus(payload));
            const res = (resultAction as any)?.payload;
            const message = res?.message || "Approved successfully";

            if (res?.status) {
                toast.success(message);
                // Update local state to reflect approved status
                setEmployees((prev) =>
                    prev.map((emp) => ({
                        ...emp,
                        approval_status: "Approved",
                        approval_status_code: 1,
                    }))
                );
            } else {
                toast.error(message || "Failed to approve timesheets");
            }
        } catch (error) {
            toast.error("An error occurred while approving timesheets");
            console.error("Error approving all:", error);
        } finally {
            setIsApprovingAll(false);
        }
    };

    const handleEmployeeClick = (employee: TimesheetApprovalEmployee) => {
        if (employee.employee_id) {
            navigate(`/m-manager-timesheet/${employee.employee_id}`, {
                state: {
                    from: "/m-notifications",
                    payPeriod: payPeriod ? `${payPeriod.start} - ${payPeriod.end}` : undefined,
                    payPeriodStart: payPeriod?.start,
                    payPeriodEnd: payPeriod?.end,
                },
            });
        }
    };

    const statusOptions = ["All", "Pending", "Approved", "Awaiting Approval", "Rejected"];

    const formatPayPeriod = (start?: string, end?: string): string => {
        if (!start || !end) return "";
        try {
            // Handle datetime format like "2025-10-13 00:00:00"
            const startDateStr = start.split(" ")[0]; // Extract date part
            const endDateStr = end.split(" ")[0]; // Extract date part

            const startDate = DateTimeUtil.DisplayDateTime(startDateStr, "MMM D", "local", true);
            const endDate = DateTimeUtil.DisplayDateTime(endDateStr, "MMM D, YYYY", "local", true);
            return `${startDate} - ${endDate}`;
        } catch {
            // Fallback: try to format as-is
            try {
                const startParts = start.split(" ")[0].split("-");
                const endParts = end.split(" ")[0].split("-");
                if (startParts.length === 3 && endParts.length === 3) {
                    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                    const startMonth = months[parseInt(startParts[1]) - 1];
                    const endMonth = months[parseInt(endParts[1]) - 1];
                    return `${startMonth} ${parseInt(startParts[2])} - ${endMonth} ${parseInt(endParts[2])}, ${
                        endParts[0]
                    }`;
                }
            } catch {}
            return `${start} - ${end}`;
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-40 w-full">
                <Loader2 className="w-8 h-8 text-gray-600 animate-spin" />
            </div>
        );
    }

    const displayPayPeriod = payPeriod
        ? DateTimeUtil.formatDateRangeFancy(payPeriod.start, payPeriod.end)
        : "Pay Period: N/A";

    // Mobile View
    if (isMobile) {
        return (
            <div className="bg-gray-50 min-h-screen">
                <div className="max-w-md mx-auto bg-white min-h-screen">
                    {/* Header */}
                    <div className="sticky top-0 bg-white border-b z-10 px-4 py-4">
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Timesheet Approvals</h1>
                        <p className="text-sm text-gray-600">{displayPayPeriod}</p>

                        {/* Filter and Approve All */}
                        <div className="flex items-center justify-between mt-4">
                            <div className="relative filter-dropdown-container">
                                <button
                                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                                    className="flex items-center space-x-1 px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                                >
                                    <span>{statusFilter}</span>
                                    <ChevronDown className="w-4 h-4" />
                                </button>
                                {showFilterDropdown && (
                                    <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-300 rounded-md shadow-lg z-20">
                                        {statusOptions.map((option) => (
                                            <button
                                                key={option}
                                                onClick={() => {
                                                    setStatusFilter(option);
                                                    setShowFilterDropdown(false);
                                                }}
                                                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                                                    statusFilter === option ? "bg-blue-50 text-blue-600" : ""
                                                }`}
                                            >
                                                {option}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <button
                                onClick={handleApproveAll}
                                disabled={isApprovingAll}
                                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {isApprovingAll ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Approving...
                                    </>
                                ) : (
                                    "Approve All"
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Employee List */}
                    <div className="divide-y divide-gray-200">
                        {isLoadingEmployees ? (
                            <div className="flex justify-center items-center py-8">
                                <Loader2 className="w-6 h-6 text-gray-600 animate-spin" />
                            </div>
                        ) : filteredEmployees.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">No employees found</div>
                        ) : (
                            filteredEmployees.map((employee, index) => {
                                const status = getStatusDisplay(
                                    employee.approval_status,
                                    employee.approval_status_code
                                );
                                return (
                                    <div
                                        key={employee.employee_id || index}
                                        className="flex items-start space-x-3 p-4 hover:bg-gray-50 cursor-pointer"
                                        onClick={() => handleEmployeeClick(employee)}
                                    >
                                        <div className="flex-shrink-0">
                                            <NgwAvatar
                                                src={employee.profile_image_url}
                                                name={employee.emp_name || ""}
                                                className="w-12 h-12"
                                            />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between">
                                                <h3 className="text-base font-medium text-gray-900">
                                                    {employee.emp_name || "Unknown Employee"}
                                                </h3>
                                                <span className={`text-sm font-medium ${status.color}`}>
                                                    {status.text}
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-600 mt-1">
                                                {formatHours(employee.total_hours)}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // Desktop View
    return (
        <div className="bg-gray-50 min-h-screen">
            <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-sm mt-4 p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Timesheet Approvals</h1>
                        <p className="text-sm text-gray-600 mt-1">{displayPayPeriod}</p>
                    </div>
                    <button
                        onClick={handleApproveAll}
                        disabled={isApprovingAll}
                        className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {isApprovingAll ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Approving...
                            </>
                        ) : (
                            "Approve All"
                        )}
                    </button>
                </div>

                {/* Filter */}
                <div className="mb-4">
                    <div className="relative inline-block filter-dropdown-container">
                        <button
                            onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                            className="flex items-center space-x-1 px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                            <span>{statusFilter}</span>
                            <ChevronDown className="w-4 h-4" />
                        </button>
                        {showFilterDropdown && (
                            <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-300 rounded-md shadow-lg z-20">
                                {statusOptions.map((option) => (
                                    <button
                                        key={option}
                                        onClick={() => {
                                            setStatusFilter(option);
                                            setShowFilterDropdown(false);
                                        }}
                                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                                            statusFilter === option ? "bg-blue-50 text-blue-600" : ""
                                        }`}
                                    >
                                        {option}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Employee
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Total Hours
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Approved By Me
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Action
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {isLoadingEmployees ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-8 text-center">
                                        <Loader2 className="w-6 h-6 text-gray-600 animate-spin mx-auto" />
                                    </td>
                                </tr>
                            ) : filteredEmployees.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                                        No employees found
                                    </td>
                                </tr>
                            ) : (
                                filteredEmployees.map((employee, index) => {
                                    const status = getStatusDisplay(
                                        employee.approval_status,
                                        employee.approval_status_code
                                    );
                                    return (
                                        <tr
                                            key={employee.employee_id || index}
                                            className="hover:bg-gray-50 cursor-pointer"
                                            onClick={() => handleEmployeeClick(employee)}
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <NgwAvatar
                                                        src={employee.profile_image_url}
                                                        name={employee.emp_name || ""}
                                                        className="w-10 h-10 mr-3"
                                                    />
                                                    <span className="text-sm font-medium text-gray-900">
                                                        {employee.emp_name || "Unknown Employee"}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {formatHours(employee.total_hours).replace(" hrs", "")}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`text-sm font-medium ${status.color}`}>
                                                    {status.text}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {employee.approved_by_me ? "Yes" : "—"}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                {(status.text === "Awaiting Approval" || status.text === "Pending") && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleEmployeeClick(employee);
                                                        }}
                                                        className="text-blue-600 hover:text-blue-800 font-medium"
                                                    >
                                                        View
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default TimesheetApprovalsNotificationDetail;
